import { clamp, uniq } from "lodash";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { FilterByOption, StateOrder } from "~/components/build/Show/lib/filterConstants";
import Bugsnag from "~/lib/Bugsnag";

export enum DockPosition {
  Right = "right",
  Bottom = "bottom",
  Center = "center",
  Full = "full",
}

const SIDEBAR_DEFAULT_WIDTH = 320;
const SIDEBAR_MAX_WIDTH = 440;
const SIDEBAR_MIN_WIDTH = 230;
const DRAWER_DEFAULT_SIZE = 50;
const DRAWER_DEFAULT_MODE = DockPosition.Center;
const ANNOTATIONS_PANEL_DEFAULT_WIDTH = 540;
const ANNOTATIONS_PANEL_MIN_WIDTH = 540;

export function clampSidebarWidth(value: number) {
  return clamp(value, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH);
}

export function clampAnnotationsPanelWidth(value: number) {
  return Math.max(value, ANNOTATIONS_PANEL_MIN_WIDTH);
}

// When feature is enabled, we won't persist the sidebar open state to the user's preferences
const nonPersistedKeys = Features.BuildUIDefaultCollapseSidebar ? ["isSidebarOpen"] : [];

const VALID_BUILD_VIEWS: ReadonlySet<string> = new Set(Object.values(BuildView));

/**
 * Resolve the persisted `defaultView` for a given pipeline, validating it is
 * a known `BuildView`.
 *
 * If the resolved value isn't a valid `BuildView` (e.g. corrupted
 * localStorage, a removed legacy value, or a future view we don't know
 * about) we fall back to `Canvas` and report the offending value to Bugsnag
 * rather than letting an unknown view propagate and break the build page.
 *
 * Note: this does not apply feature-flag fallbacks (e.g. Waterfall →
 * Table). That happens in `getDefaultView` once we have a valid view.
 */
export function resolveDefaultView(
  defaultView: BuildView | Record<string, BuildView>,
  pipeline: string,
): BuildView {
  let view: unknown;

  if (typeof defaultView === "string") {
    view = defaultView;
  } else {
    view = defaultView[pipeline] || defaultView["*"];
  }

  if (view === undefined) {
    return BuildView.Canvas;
  }

  if (typeof view === "string" && VALID_BUILD_VIEWS.has(view)) {
    return view as BuildView;
  }

  Bugsnag.notify(
    `[useBuildPreferencesStore] Unrecognised persisted defaultView value: ${JSON.stringify(view)}. Falling back to Canvas.`,
  );
  return BuildView.Canvas;
}

/**
 * Migrates a persisted `build-preferences` payload to the latest version.
 *
 * v1 → v2: Rewrites the legacy `defaultView: "STEPS"` value (removed in
 * PR #29209) to `BuildView.Canvas`, both as a top-level string and inside
 * the per-pipeline record form.
 */
export function migrateBuildPreferences(persistedState: unknown, version: number): unknown {
  if (!persistedState || typeof persistedState !== "object") {
    return persistedState;
  }

  const state = persistedState as { defaultView?: BuildView | Record<string, BuildView> };

  if (version < 2 && state.defaultView !== undefined) {
    const replaceSteps = (view: unknown): BuildView =>
      view === "STEPS" ? BuildView.Canvas : (view as BuildView);

    if (typeof state.defaultView === "string") {
      state.defaultView = replaceSteps(state.defaultView);
    } else if (state.defaultView && typeof state.defaultView === "object") {
      state.defaultView = Object.fromEntries(
        Object.entries(state.defaultView).map(([pipeline, view]) => [pipeline, replaceSteps(view)]),
      );
    }
  }

  return state;
}

interface BuildPreferences {
  /**
   * Default view for the build page, which can be overridden by the URL.
   * This is used to set the initial view when the page loads.
   *
   * If a string is provided, it will be used as the default view for all pipelines.
   * If an object is provided, it will map pipeline names to their respective default views.
   */
  defaultView: Record<string, BuildView> | BuildView;
  getDefaultView: (pipeline: string) => BuildView;
  setDefaultView: (view: BuildView, pipeline?: string) => void;

  dockPosition: DockPosition;
  setDockPosition: (dockPosition: DockPosition) => void;

  /**
   * Resized sidebar width (as pixels)
   */
  sidebarWidth?: number;
  setSidebarWidth: (width: number) => void;
  isSidebarOpen?: boolean;
  toggleSidebar: () => void;

  /**
   * Resized drawer height (in percentage)
   */
  resizedDrawerHeight?: number;
  setResizedDrawerHeight: (height: number) => void;
  resetDrawerHeight: () => void;

  /**
   * Resized drawer width (in percentage)
   */
  resizedDrawerWidth?: number;
  setResizedDrawerWidth: (width: number) => void;
  resetDrawerWidth: () => void;

  /**
   * Table view sort column and direction
   */
  sortColumn?: string | null;
  setSortColumn: (column: string | null) => void;
  sortDirection?: "asc" | "desc" | null;
  setSortDirection: (direction: "asc" | "desc" | null) => void;
  cycleSort: (column: string) => void;

  /**
   * Expanded/collapsed states (ie. failed, passed, etc.) in the sidebar
   */
  expandedStates?: string[];
  setExpandedStates: (states: string[]) => void;
  expandState: (state: string) => void;

  /**
   * Column widths
   */
  columnWidths: Record<string, number>;
  setColumnWidth: (key: string, width: number) => void;
  resetColumnWidths: () => void;

  /**
   * Show retried jobs (previous job attempts that were retried) in the build table
   */
  showRetriedJobs: boolean;
  setShowRetriedJobs: (show: boolean) => void;

  /**
   * Show skipped/ignored steps in the build step list
   */
  showSkippedSteps: boolean;
  setShowSkippedSteps: (show: boolean) => void;

  /**
   * Annotations panel (right-docked) width
   */
  annotationsPanelWidth: number;
  setAnnotationsPanelWidth: (width: number) => void;

  /**
   * Annotations panel (right-docked) open/closed preference.
   *
   * This is the persisted preference. The effective open state at runtime
   * is determined by `useAnnotationsPanel`, which overrides this on initial
   * render of a build that has annotations.
   */
  annotationsPanelOpen: boolean;
  setAnnotationsPanelOpen: (open: boolean) => void;
}

/**
 * A store for build page preferences persisted into local storage to ensure
 * the user's preferences are remembered between sessions.
 */
export const useBuildPreferencesStore = create<BuildPreferences>()(
  persist(
    (set, get) => ({
      dockPosition: DRAWER_DEFAULT_MODE,
      resizedDrawerHeight: DRAWER_DEFAULT_SIZE,
      resizedDrawerWidth: DRAWER_DEFAULT_SIZE,
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      isSidebarOpen: !Features.BuildUIDefaultCollapseSidebar,

      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

      // Default all states headers to be expanded with the exception of "Skipped"
      expandedStates: StateOrder.filter((state) => state !== FilterByOption.Ignored),

      setExpandedStates: (expandedStates: string[]) => set(() => ({ expandedStates })),
      expandState: (expandedState: string) =>
        set((state) => ({
          expandedStates: uniq([...(state.expandedStates ?? []), expandedState]),
        })),

      setSortColumn: (column: string | null) => set(() => ({ sortColumn: column })),
      setSortDirection: (direction: "asc" | "desc" | null) =>
        set(() => ({ sortDirection: direction })),
      cycleSort: (column: string) => {
        const { sortColumn: currentColumn, sortDirection: currentDirection } = get();

        if (column !== currentColumn) {
          set({ sortColumn: column, sortDirection: "asc" });
          return;
        }

        if (currentDirection === "asc") {
          set({ sortDirection: "desc" });
          return;
        }

        if (currentDirection === "desc") {
          set({ sortColumn: null, sortDirection: null });
          return;
        }

        // Inconsistent state (column set but direction missing) — start fresh
        set({ sortDirection: "asc" });
      },

      setSidebarWidth: (width: number) => {
        return set(() => ({ sidebarWidth: clampSidebarWidth(width) }));
      },

      setDockPosition: (dock: DockPosition) => set(() => ({ dockPosition: dock })),

      resetDrawerHeight: () => set(() => ({ resizedDrawerHeight: DRAWER_DEFAULT_SIZE })),
      setResizedDrawerHeight: (height: number) => set(() => ({ resizedDrawerHeight: height })),

      resetDrawerWidth: () => set(() => ({ resizedDrawerWidth: DRAWER_DEFAULT_SIZE })),
      setResizedDrawerWidth: (width: number) => set(() => ({ resizedDrawerWidth: width })),

      columnWidths: {},
      setColumnWidth: (key, width) =>
        set((state) => ({
          columnWidths: { ...state.columnWidths, [key]: width },
        })),
      resetColumnWidths: () => set({ columnWidths: {} }),

      showRetriedJobs: false,
      setShowRetriedJobs: (show: boolean) => set(() => ({ showRetriedJobs: show })),

      showSkippedSteps: false,
      setShowSkippedSteps: (show: boolean) => set(() => ({ showSkippedSteps: show })),

      annotationsPanelWidth: ANNOTATIONS_PANEL_DEFAULT_WIDTH,
      setAnnotationsPanelWidth: (width: number) =>
        set(() => ({ annotationsPanelWidth: clampAnnotationsPanelWidth(width) })),

      annotationsPanelOpen: false,
      setAnnotationsPanelOpen: (open: boolean) => set(() => ({ annotationsPanelOpen: open })),

      defaultView: BuildView.Canvas,

      /**
       * Get the default view for navigation, based on stored preferences and feature flag fallbacks
       */
      getDefaultView: (pipeline: string) => {
        const view = resolveDefaultView(get().defaultView, pipeline);

        if (view === BuildView.Waterfall && Features.DisableStepsWaterfallView) {
          // Fall back to table view if the user has set Waterfall as their preference
          // We won't persist this to their preferences as they may belong to other
          // organizations where this would be surprising
          return BuildView.Table;
        }

        if (view === BuildView.Canvas && Features.DisableStepsCanvasView) {
          // Fall back to table view if the user has set Canvas as their preference
          // We won't persist this to their preferences as they may belong to other
          // organizations where this would be surprising
          return BuildView.Table;
        }

        if (view === BuildView.Annotations && Features.BuildPageDockedAnnotations) {
          // Annotations view is being deprecated in favor of the docked annotations.
          // Temporary fallback to canvas view if the user has set Annotations as their preference
          // We will write a zustand migration to remove this preference for all users as part of wider rollout process
          return BuildView.Canvas;
        }

        return view;
      },
      setDefaultView: (view: BuildView, pipeline?: string) => {
        if (pipeline) {
          set((state) => {
            if (typeof state.defaultView === "string") {
              return {
                defaultView: {
                  [pipeline]: view,
                  // Preserve the default view set for all other pipelines
                  "*": state.defaultView,
                },
              };
            }

            return {
              defaultView: {
                ...state.defaultView,
                [pipeline]: view,
              },
            };
          });
        } else {
          set(() => ({
            defaultView: view,
          }));
        }
      },
    }),
    {
      name: "build-preferences",
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persistedState, version) =>
        migrateBuildPreferences(persistedState, version) as BuildPreferences,
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !nonPersistedKeys.includes(key)),
        ),
    },
  ),
);
